<template>
  <div class="space-y-6">
    <header class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-white">Consignees</h1>
        <p class="text-sm text-slate-400 mt-1">
          Manage the consignee directory used in reserve and checkout workflows.
        </p>
      </div>
      <button class="btn-primary inline-flex items-center gap-2" @click="openCreateModal">
        <span class="text-lg leading-none">+</span>
        <span>Create New Consignee</span>
      </button>
    </header>

    <section class="panel-neo panel-neo--glassier p-4 rounded-2xl">
      <input
        v-model="search"
        type="text"
        placeholder="Search by name, email, phone, city, state, or organization"
        class="input-dark w-[300px] max-w-full"
      />
    </section>

    <section class="panel-neo panel-neo--glassier p-4 rounded-2xl">
      <div v-if="loading" class="py-12 text-center text-slate-400">Loading consignees...</div>
      <div v-else-if="error" class="py-12 text-center text-rose-400">{{ error }}</div>
      <div v-else-if="filteredConsignees.length === 0" class="py-12 text-center text-slate-400">
        No consignees found.
      </div>
      <div v-else class="overflow-x-auto">
        <div
          class="max-h-[500px] overflow-y-auto pr-1 rounded-xl border border-slate-800/80 shadow-[0_14px_30px_rgba(2,6,23,0.38)]"
        >
          <table class="w-full text-sm text-slate-200">
            <thead class="sticky top-0 bg-slate-950/90 backdrop-blur">
              <tr class="text-left text-xs uppercase tracking-[0.15em] text-slate-400 border-b border-slate-700/60">
                <th class="py-3 pr-4">Name</th>
                <th class="py-3 pr-4">Organization</th>
                <th class="py-3 pr-4">Email</th>
                <th class="py-3 pr-4">Phone</th>
                <th class="py-3 pr-4">Location</th>
                <th class="py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in filteredConsignees"
                :key="c.id"
                class="border-b border-slate-800/60 hover:bg-slate-900/40"
              >
                <td class="py-3 pr-4">
                  <div class="font-medium text-white">{{ c.name || '—' }}</div>
                  <div class="text-xs text-slate-500">{{ c.title || 'No title' }}</div>
                </td>
                <td class="py-3 pr-4">{{ c.organization_name || '—' }}</td>
                <td class="py-3 pr-4">{{ c.email || '—' }}</td>
                <td class="py-3 pr-4">{{ c.phone || '—' }}</td>
                <td class="py-3 pr-4">{{ formatLocation(c) }}</td>
                <td class="py-3">
                  <button
                    class="px-3 py-1.5 rounded border border-slate-600 text-slate-200 hover:border-slate-400 hover:text-white transition-colors"
                    @click="openEditModal(c)"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <p v-if="saveSuccess" class="text-sm text-emerald-400">{{ saveSuccess }}</p>
    <p v-if="saveError" class="text-sm text-rose-400">{{ saveError }}</p>

    <teleport to="body">
      <div
        v-if="showConsigneeModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-[70]"
      >
        <div
          class="glass-strong rounded-xl p-5 w-full max-w-5xl shadow-2xl border border-slate-700/80 max-h-[88vh] overflow-y-auto"
        >
          <h2 class="text-xl font-bold text-white mb-1">
            {{ modalMode === 'edit' ? 'Edit Consignee' : 'Create New Consignee' }}
          </h2>
          <p class="text-slate-400 text-sm mb-4">
            Keep consignee records clean and reusable across reserve and checkout workflows.
          </p>

          <section class="mb-4 rounded-lg border border-slate-800/80 bg-slate-900/35 p-4">
            <h3 class="text-slate-300 font-semibold mb-2">Organization</h3>
            <div class="flex items-center gap-4 text-sm text-slate-300 mb-3">
              <label class="inline-flex items-center gap-2">
                <input v-model="organizationMode" type="radio" value="existing" />
                Use existing organization
              </label>
              <label class="inline-flex items-center gap-2">
                <input v-model="organizationMode" type="radio" value="new" />
                Create organization
              </label>
            </div>

            <div v-if="organizationMode === 'existing'" class="w-[200px] max-w-full">
              <label class="block text-slate-300 text-xs mb-1">Organization</label>
              <select v-model.number="form.organization_id" class="consignee-input w-full">
                <option class="bg-slate-900 text-slate-100" :value="undefined">None</option>
                <option
                  v-for="o in organizations"
                  :key="o.id"
                  :value="o.id"
                  class="bg-slate-900 text-slate-100"
                >
                  {{ o.name }}
                </option>
              </select>
            </div>

            <div v-else class="grid grid-cols-[minmax(240px,1fr)_220px] gap-2 max-w-[640px]">
              <div>
                <label class="block text-slate-300 text-xs mb-1">Organization</label>
                <input v-model="newOrganizationName" type="text" class="consignee-input w-full" />
              </div>
              <div>
                <label class="block text-slate-300 text-xs mb-1">Organization Type</label>
                <select v-model="newOrganizationType" class="consignee-input w-full">
                  <option class="bg-slate-900 text-slate-100" value="customer">Customer</option>
                  <option class="bg-slate-900 text-slate-100" value="prospect">Prospect</option>
                  <option class="bg-slate-900 text-slate-100" value="partner">Partner</option>
                  <option class="bg-slate-900 text-slate-100" value="supplier">Supplier</option>
                </select>
              </div>
            </div>
          </section>

          <section class="rounded-lg border border-slate-800/80 bg-slate-900/35 p-4">
            <h3 class="text-slate-300 font-semibold mb-2">Consignee</h3>
            <div class="grid grid-cols-2 gap-2">
              <input v-model="form.name" placeholder="Consignee Name *" type="text" class="consignee-input" />
              <input v-model="form.title" placeholder="Title" type="text" class="consignee-input" />
              <input v-model="form.email" placeholder="Email" type="email" class="consignee-input" />
              <input v-model="form.phone" placeholder="Phone" type="tel" class="consignee-input" />
              <div class="col-span-2 flex items-center gap-2">
                <input
                  v-model="form.address1"
                  placeholder="Address 1"
                  type="text"
                  class="consignee-input flex-1 min-w-0"
                />
                <input
                  v-model="form.address2"
                  placeholder="Address 2"
                  type="text"
                  class="consignee-input w-[105px]"
                />
              </div>
              <div class="col-span-2 flex items-center gap-2">
                <input
                  v-model="form.city"
                  placeholder="City"
                  type="text"
                  class="consignee-input flex-1 min-w-0"
                />
                <select v-model="form.state" class="consignee-input uppercase w-[72px] appearance-none">
                  <option class="bg-slate-900 text-slate-100" value="" disabled>ST</option>
                  <option
                    v-for="abbr in US_STATE_ABBREVIATIONS"
                    :key="`consignee-${abbr}`"
                    :value="abbr"
                    class="bg-slate-900 text-slate-100"
                  >
                    {{ abbr }}
                  </option>
                </select>
                <input v-model="form.zip" placeholder="ZIP" type="text" class="consignee-input w-[115px]" />
              </div>
            </div>

            <div class="mt-4">
              <label class="inline-flex items-center gap-2 text-slate-300 text-sm">
                <input v-model="form.shipping_same_as_consignee" type="checkbox" />
                Shipping address same as Consignee address
              </label>
            </div>

            <div v-if="!form.shipping_same_as_consignee" class="mt-3">
              <h4 class="text-slate-300 font-semibold mb-2">Shipping</h4>
              <div class="grid grid-cols-2 gap-2">
                <div class="col-span-2 flex items-center gap-2">
                  <input
                    v-model="form.shipping_address1"
                    placeholder="Address 1"
                    type="text"
                    class="consignee-input flex-1 min-w-0"
                  />
                  <input
                    v-model="form.shipping_address2"
                    placeholder="Address 2"
                    type="text"
                    class="consignee-input w-[105px]"
                  />
                </div>
                <div class="col-span-2 flex items-center gap-2">
                  <input
                    v-model="form.shipping_city"
                    placeholder="City"
                    type="text"
                    class="consignee-input flex-1 min-w-0"
                  />
                  <select v-model="form.shipping_state" class="consignee-input uppercase w-[72px] appearance-none">
                    <option class="bg-slate-900 text-slate-100" value="" disabled>ST</option>
                    <option
                      v-for="abbr in US_STATE_ABBREVIATIONS"
                      :key="`shipping-${abbr}`"
                      :value="abbr"
                      class="bg-slate-900 text-slate-100"
                    >
                      {{ abbr }}
                    </option>
                  </select>
                  <input
                    v-model="form.shipping_zip"
                    placeholder="ZIP"
                    type="text"
                    class="consignee-input w-[115px]"
                  />
                </div>
              </div>
            </div>
          </section>

          <div class="mt-6 flex justify-end gap-3">
            <button
              class="px-4 py-2 rounded bg-slate-800 border border-slate-700 text-white"
              :disabled="saving"
              @click="closeConsigneeModal"
            >
              Cancel
            </button>
            <button class="btn-primary" :disabled="saving" @click="saveConsignee">
              {{ saving ? 'Saving...' : modalMode === 'edit' ? 'Save Changes' : 'Create Consignee' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { supabase } from '@/lib/supabaseClient'
const DATA_UPDATED_EVENT = 'testdrive:data-updated'

type ConsigneeRow = {
  id: number
  organization_id: number | null
  name: string | null
  title: string | null
  email: string | null
  phone: string | null
  address1: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  shipping_same_as_consignee: boolean | null
  shipping_address1: string | null
  shipping_address2: string | null
  shipping_city: string | null
  shipping_state: string | null
  shipping_zip: string | null
  organization_name: string | null
}

const loading = ref(true)
const saving = ref(false)
const error = ref('')
const saveError = ref('')
const saveSuccess = ref('')
const search = ref('')
const consignees = ref<ConsigneeRow[]>([])
const organizations = ref<Array<{ id: number; name: string }>>([])
const showConsigneeModal = ref(false)
const modalMode = ref<'create' | 'edit'>('create')

const organizationMode = ref<'existing' | 'new'>('existing')
const newOrganizationName = ref('')
const newOrganizationType = ref<'customer' | 'prospect' | 'partner' | 'supplier'>('customer')

const form = reactive({
  id: undefined as number | undefined,
  organization_id: undefined as number | undefined,
  name: '',
  title: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  shipping_same_as_consignee: true,
  shipping_address1: '',
  shipping_address2: '',
  shipping_city: '',
  shipping_state: '',
  shipping_zip: '',
})

const US_STATE_ABBREVIATIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]

const filteredConsignees = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return consignees.value
  return consignees.value.filter((c) => {
    return [c.name, c.email, c.phone, c.city, c.state, c.organization_name]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(term))
  })
})

function formatLocation(c: ConsigneeRow) {
  const cityState = [c.city, c.state].filter(Boolean).join(', ')
  if (!cityState && !c.zip) return '—'
  return [cityState, c.zip].filter(Boolean).join(' ')
}

function resetForm() {
  form.id = undefined
  form.organization_id = undefined
  form.name = ''
  form.title = ''
  form.email = ''
  form.phone = ''
  form.address1 = ''
  form.address2 = ''
  form.city = ''
  form.state = ''
  form.zip = ''
  form.shipping_same_as_consignee = true
  form.shipping_address1 = ''
  form.shipping_address2 = ''
  form.shipping_city = ''
  form.shipping_state = ''
  form.shipping_zip = ''
  organizationMode.value = 'existing'
  newOrganizationName.value = ''
  newOrganizationType.value = 'customer'
  saveError.value = ''
  saveSuccess.value = ''
}

function startCreate() {
  resetForm()
}

function startEdit(c: ConsigneeRow) {
  form.id = c.id
  form.organization_id = c.organization_id ?? undefined
  form.name = c.name ?? ''
  form.title = c.title ?? ''
  form.email = c.email ?? ''
  form.phone = c.phone ?? ''
  form.address1 = c.address1 ?? ''
  form.address2 = c.address2 ?? ''
  form.city = c.city ?? ''
  form.state = c.state ?? ''
  form.zip = c.zip ?? ''
  form.shipping_same_as_consignee = c.shipping_same_as_consignee ?? true
  form.shipping_address1 = c.shipping_address1 ?? ''
  form.shipping_address2 = c.shipping_address2 ?? ''
  form.shipping_city = c.shipping_city ?? ''
  form.shipping_state = c.shipping_state ?? ''
  form.shipping_zip = c.shipping_zip ?? ''
  organizationMode.value = 'existing'
  saveError.value = ''
  saveSuccess.value = ''
}

function openCreateModal() {
  startCreate()
  modalMode.value = 'create'
  showConsigneeModal.value = true
}

function openEditModal(c: ConsigneeRow) {
  startEdit(c)
  modalMode.value = 'edit'
  showConsigneeModal.value = true
}

function closeConsigneeModal() {
  showConsigneeModal.value = false
}

async function loadOrganizations() {
  const { data, error: orgError } = await supabase
    .schema('app' as any)
    .from('organizations')
    .select('id, name')
    .order('name')
  if (orgError) throw orgError
  organizations.value = data || []
}

async function loadConsignees() {
  const { data, error: loadErr } = await supabase
    .schema('app' as any)
    .from('contacts')
    .select('*, organizations(name)')
    .order('name')
  if (loadErr) throw loadErr
  consignees.value = (data || []).map((row: any) => ({
    id: row.id,
    organization_id: row.organization_id ?? null,
    name: row.name ?? null,
    title: row.title ?? null,
    email: row.email ?? null,
    phone: row.phone ?? null,
    address1: row.address1 ?? null,
    address2: row.address2 ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    zip: row.zip ?? null,
    shipping_same_as_consignee: row.shipping_same_as_consignee ?? null,
    shipping_address1: row.shipping_address1 ?? null,
    shipping_address2: row.shipping_address2 ?? null,
    shipping_city: row.shipping_city ?? null,
    shipping_state: row.shipping_state ?? null,
    shipping_zip: row.shipping_zip ?? null,
    organization_name: row.organizations?.name ?? null,
  }))
}

async function resolveOrganizationId(): Promise<number | null> {
  if (organizationMode.value === 'existing') {
    return form.organization_id ?? null
  }
  const orgName = newOrganizationName.value.trim()
  if (!orgName) return null
  const { data, error: orgError } = await supabase
    .schema('app' as any)
    .from('organizations')
    .insert({ name: orgName, type: newOrganizationType.value })
    .select('id')
    .single()
  if (orgError) throw orgError
  return data.id
}

async function saveConsignee() {
  saveError.value = ''
  saveSuccess.value = ''
  if (!form.name.trim()) {
    saveError.value = 'Consignee name is required.'
    return
  }

  saving.value = true
  try {
    const organizationId = await resolveOrganizationId()
    const payload = {
      organization_id: organizationId,
      name: form.name.trim(),
      title: form.title.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address1: form.address1.trim() || null,
      address2: form.address2.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim().toUpperCase() || null,
      zip: form.zip.trim() || null,
      shipping_same_as_consignee: form.shipping_same_as_consignee,
      shipping_address1: form.shipping_same_as_consignee ? null : (form.shipping_address1.trim() || null),
      shipping_address2: form.shipping_same_as_consignee ? null : (form.shipping_address2.trim() || null),
      shipping_city: form.shipping_same_as_consignee ? null : (form.shipping_city.trim() || null),
      shipping_state: form.shipping_same_as_consignee ? null : (form.shipping_state.trim().toUpperCase() || null),
      shipping_zip: form.shipping_same_as_consignee ? null : (form.shipping_zip.trim() || null),
    }

    if (form.id) {
      const { error: updateError } = await supabase
        .schema('app' as any)
        .from('contacts')
        .update(payload)
        .eq('id', form.id)
      if (updateError) throw updateError
      saveSuccess.value = 'Consignee updated.'
    } else {
      const { error: createError } = await supabase.schema('app' as any).from('contacts').insert(payload)
      if (createError) throw createError
      saveSuccess.value = 'Consignee created.'
    }

    await Promise.all([loadConsignees(), loadOrganizations()])
    showConsigneeModal.value = false
    resetForm()
  } catch (e: any) {
    saveError.value = e?.message || 'Failed to save consignee.'
  } finally {
    saving.value = false
  }
}

watch(
  () => [
    form.shipping_same_as_consignee,
    form.address1,
    form.address2,
    form.city,
    form.state,
    form.zip,
  ],
  () => {
    if (!form.shipping_same_as_consignee) return
    form.shipping_address1 = form.address1
    form.shipping_address2 = form.address2
    form.shipping_city = form.city
    form.shipping_state = form.state
    form.shipping_zip = form.zip
  },
  { immediate: true },
)

onMounted(async () => {
  loading.value = true
  error.value = ''
  try {
    await Promise.all([loadConsignees(), loadOrganizations()])
  } catch (e: any) {
    error.value = e?.message || 'Failed to load consignee directory.'
  } finally {
    loading.value = false
  }
  window.addEventListener(DATA_UPDATED_EVENT, handleDataUpdated)
})

onBeforeUnmount(() => {
  window.removeEventListener(DATA_UPDATED_EVENT, handleDataUpdated)
})

async function handleDataUpdated() {
  try {
    await Promise.all([loadConsignees(), loadOrganizations()])
  } catch {
    // Non-fatal for passive refresh events.
  }
}
</script>

<style scoped>
.consignee-input {
  padding: 0.5rem 0.75rem;
  background: rgba(30, 41, 59, 0.72);
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 0.375rem;
  color: #f8fafc;
}

.consignee-input::placeholder {
  color: rgba(148, 163, 184, 0.88);
}

.consignee-input:focus {
  outline: none;
  border-color: rgba(96, 165, 250, 0.85);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
</style>
